import React, { useState, useMemo, useEffect, useRef } from 'react';
import './PartNumbersList.css';
import { comparePartNumbers } from '../utils/reconSort';

/*
 * PartNumbersList (shared)
 * ------------------------
 * Relocated from src/features/inventory/components/PartNumbersList.jsx and
 * extended with a real inline-edit affordance. Self-contained styling with
 * the `pnl-*` prefix and design-token-only CSS — safe to host inside a
 * recon SortableSection without leaking parent rules.
 *
 * Add-row ownership: host (controlled inputs via setNewPn / setNewPnType /
 * setNewPnDesc), unchanged from the legacy contract.
 *
 * Edit-row ownership: component-internal. Only one row may be in edit mode at
 * a time. Save calls onEdit(id, {part_number, part_number_type,
 * part_description}); the host is expected to await the backend write and may
 * rethrow to keep the row in edit mode after an error.
 *
 * Prop contract:
 *   partNumbers (array, required) — RENDERED in comparePartNumbers order
 *               (reconSort.js), the same display sort AssetTagsList applies
 *               to tags. The array itself is never reordered.
 *   loading     (bool, default false)
 *   error       (string, optional)
 *   readOnly    (bool, default false) — suppresses add/edit/delete chrome
 *   onAdd       (fn, required if !readOnly)
 *   onEdit      (fn(id, {part_number, part_number_type, part_description})
 *                — required if !readOnly)
 *   onDelete    (fn(id) — required if !readOnly)
 *   newPn / setNewPn / newPnType / setNewPnType / newPnDesc / setNewPnDesc
 *               — host-owned add-row state, passed through
 *   partNumberTypeOptions
 *               — reused for both add-row and edit-row selects. Falls back to
 *                 the hardcoded list when undefined, matching legacy behavior.
 *   showTypeControl
 *               — (bool, default true) renders the add-row and edit-row Type
 *                 selects. The field zone passes false; every other call site
 *                 inherits current behavior. editPnType is unaffected either
 *                 way, so a hidden select still saves the row's existing type.
 *   onEditingChange
 *               — (fn(bool) | null, default null) fired when the internal
 *                 edit row opens or closes. Reports THAT an edit is open,
 *                 never what it says — editPn stays component-internal.
 *                 The field modal uses it to refuse a Save that would
 *                 silently omit an uncommitted row (F-44b).
 *   pointerCapture
 *               — (bool, default false) routes this component's two COMMIT
 *                 buttons (edit-row Save, add-row Add) through
 *                 setPointerCapture on pointerdown, so pointerup still lands
 *                 on the button after layout moves it mid-gesture.
 *                 F-58: on iOS the keyboard dismisses when a button is
 *                 TOUCHED, the host dialog grows, and the button travels
 *                 down between pointerdown and liftoff — pointerup hits
 *                 empty space and NO click is dispatched at all. Witnessed
 *                 on TEST 8f514f5, 2026-09-02, iOS handset: with the
 *                 keyboard UP, Add on the part-number add row did nothing;
 *                 with the keyboard DOWN the same tap worked. Desktop
 *                 Chrome cannot reproduce it — no keyboard, no viewport
 *                 motion.
 *                 ⛔ DEFAULT OFF IS LOAD-BEARING. onClick stays ungated (the
 *                 established F-58 shape — keyboard, programmatic and
 *                 assistive activation dispatch a click with no pointer
 *                 sequence, and gating it would make these buttons dead on
 *                 those paths). So where the button does NOT move, one
 *                 activation delivers BOTH a pointerup and a click. The
 *                 field modal absorbs that: its handlers write through
 *                 updateChildSet, a full-set replace computed from the same
 *                 stale base, so the second write lands the same set. The
 *                 four legacy screens do NOT absorb it — ToolDetailScreen's
 *                 handleAddPartNumber POSTs a mint, so a double-fire would
 *                 create TWO part-number documents. Only the field zone
 *                 opts in; every other call site must inherit the off path.
 */

// Fallback when ReferenceData (field_name='part_number_type') hasn't loaded /
// isn't seeded. Kept in sync with the refdata seed (the full live type set,
// incl. COMPONENT_PN / INV_OEM_PN / TIER_1 / INVENTORY) so the selector is never
// short of the real vocabulary even on the fallback path. Hosts that pass
// partNumberTypeOptions (e.g. ToolDetailScreen → refOptions.part_number_type)
// override this.
const DEFAULT_TYPE_OPTIONS = [
  'TBD', 'FG', 'SUPPLIER', 'COMPONENT', 'COMPONENT_PN', 'PLM', 'BASE',
  'PRODUCTION', 'SERVICE', 'INV_OEM_PN', 'TIER_1', 'INVENTORY',
];

export default function PartNumbersList({
  partNumbers,
  loading = false,
  error,
  readOnly = false,
  onAdd,
  onEdit,
  onDelete,
  newPn,
  setNewPn,
  newPnType,
  setNewPnType,
  newPnDesc,
  setNewPnDesc,
  partNumberTypeOptions,
  showTypeControl = true,
  // F-44b: OPTIONAL and defaulted — this component has 5 call sites, 4 of
  // them legacy screens that pass nothing here, and
  // DispositionDetailPane passes only two props in total.
  onEditingChange = null,
  // F-58: OPTIONAL and defaulted OFF — same 5 call sites, and only the field
  // zone (FieldRecordModal) opts in. ToolDetailScreen, LifecycleDetailPanel,
  // DisposalDetailPanel and DispositionDetailPane pass nothing here and
  // change by ZERO lines. The direction of this default is the whole safety
  // argument, not house style — see the prop contract above.
  pointerCapture = false,
}) {
  // Prefer ReferenceData-supplied options; fall back to the full default when the
  // prop is absent OR an empty array (an empty [] is truthy in JS — guard length).
  const typeOptions = (Array.isArray(partNumberTypeOptions) && partNumberTypeOptions.length)
    ? partNumberTypeOptions
    : DEFAULT_TYPE_OPTIONS;
  const canEdit = !readOnly && typeof onEdit === 'function';

  // F-58 capture props for ONE commit button, or {} when opted out — the off
  // path adds no handler at all, so a legacy call site keeps its exact
  // current event surface. A factory rather than one inline pair because the
  // two commit buttons have DIFFERENT pointerup targets (the local saveEdit
  // vs the onAdd prop), and this keeps the opt-in check in one place instead
  // of two drifting copies.
  // Release BEFORE firing: the handler should run with the pointer already
  // let go, matching the field modal's Save/Create pair.
  // fn?.() — onAdd is a PROP and may be absent; saveEdit never is.
  const captureProps = (fn) => (pointerCapture ? {
    onPointerDown: (e) => {
      // Throws if the pointer is already released; nothing to do then.
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* already released */ }
    },
    onPointerUp: (e) => {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      fn?.();
    },
  } : {});

  const [editingId, setEditingId] = useState(null);
  const [editPn, setEditPn] = useState('');
  const [editPnType, setEditPnType] = useState('');
  const [editPnDesc, setEditPnDesc] = useState('');

  // Phase 3d: an edit row still open at UNMOUNT is reported closed. cancelEdit
  // was the only false, so a list torn down mid-edit — the field modal
  // closing, or its ALL → INV switch — left the host's childEditPending true
  // for good, refusing every later Save over an edit row nobody could see.
  // editOpenRef tracks what was REPORTED (set beside each call), not
  // editingId, which can be an undefined id. Both refs are read by a
  // once-only cleanup, which would otherwise see the first render's values.
  // No callback, no call: the call sites that pass none are untouched.
  const editOpenRef = useRef(false);
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;
  useEffect(() => () => {
    if (editOpenRef.current && onEditingChangeRef.current) onEditingChangeRef.current(false);
  }, []);

  // Display-only sort, mirroring AssetTagsList: a sorted COPY for render, so
  // no host's stored order is ever reordered. Edit/delete still fire by id.
  // Every row is sorted the same way — type rank (PN_TYPE_ORDER, unknown
  // types next, TBD last), then part_number — whatever its id.
  const sortedPartNumbers = useMemo(
    () => [...partNumbers].sort(comparePartNumbers), [partNumbers]);

  function startEdit(pn) {
    if (!canEdit) return;
    setEditingId(pn.id);
    setEditPn(pn.part_number || '');
    setEditPnType(pn.part_number_type || '');
    setEditPnDesc(pn.part_description || '');
    editOpenRef.current = true;
    if (onEditingChange) onEditingChange(true);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditPn('');
    setEditPnType('');
    setEditPnDesc('');
    // F-44b: one call here covers BOTH exits. The Cancel button calls this
    // directly, and saveEdit's success path calls it too (see below), so a
    // second call in saveEdit would be redundant, not safer.
    editOpenRef.current = false;
    if (onEditingChange) onEditingChange(false);
  }

  async function saveEdit() {
    if (!canEdit || !editPn.trim()) return;
    try {
      await onEdit(editingId, {
        part_number: editPn.trim(),
        part_number_type: editPnType || null,
        part_description: editPnDesc.trim() || null,
      });
      cancelEdit();
    } catch {
      // Host surfaces the error via the `error` prop. Stay in edit mode so
      // the user can retry without re-typing.
    }
  }

  return (
    <>
      {error && <p className="pnl-error">{error}</p>}
      <div className="pnl-list">
        {loading && <p className="pnl-loading">Loading part numbers...</p>}
        {!loading && partNumbers.length === 0 && (
          <p className="pnl-empty">No part numbers added.</p>
        )}
        {!loading && sortedPartNumbers.map(pn => {
          // editingId starts null, so a bare `editingId === pn.id` put any
          // row with id == null into edit mode on first render — inputs and
          // Save/Cancel, even readOnly. null means "nothing is being edited".
          if (editingId !== null && editingId === pn.id) {
            return (
              <div key={pn.id} className="pnl-row pnl-row-edit">
                <input
                  type="text"
                  className="pnl-input pnl-input-grow"
                  placeholder="Part number *"
                  value={editPn}
                  onChange={e => setEditPn(e.target.value)}
                />
                {showTypeControl && (
                  <select
                    className="pnl-select pnl-input-sm"
                    value={editPnType}
                    onChange={e => setEditPnType(e.target.value)}
                  >
                    <option value="">Type</option>
                    {typeOptions.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                )}
                <input
                  type="text"
                  className="pnl-input pnl-input-grow"
                  placeholder="Description (optional)"
                  value={editPnDesc}
                  onChange={e => setEditPnDesc(e.target.value)}
                />
                <button
                  type="button"
                  className="pnl-btn-save"
                  onClick={saveEdit}
                  {...captureProps(saveEdit)}
                  disabled={!editPn.trim()}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="pnl-btn-cancel"
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
              </div>
            );
          }
          return (
            <div key={pn.id} className="pnl-row">
              <div
                className={`pnl-info${canEdit ? ' pnl-info-editable' : ''}`}
                onClick={canEdit ? () => startEdit(pn) : undefined}
              >
                <span className="pnl-val">{pn.part_number}</span>
                {pn.part_number_type && (
                  <span className="pnl-badge">{pn.part_number_type}</span>
                )}
                {pn.part_description && (
                  <span className="pnl-desc">{pn.part_description}</span>
                )}
              </div>
              {!readOnly && (
                <button
                  type="button"
                  className="pnl-del"
                  onClick={() => onDelete(pn.id)}
                  aria-label="Delete"
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
        {!readOnly && (
          <div className="pnl-add">
            <input
              type="text"
              className="pnl-input pnl-input-grow"
              placeholder="Part number"
              value={newPn}
              onChange={e => setNewPn(e.target.value)}
            />
            {showTypeControl && (
              <select
                className="pnl-select pnl-input-sm"
                value={newPnType}
                onChange={e => setNewPnType(e.target.value)}
              >
                <option value="">Type</option>
                {typeOptions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            )}
            <input
              type="text"
              className="pnl-input pnl-input-grow"
              placeholder="Description (optional)"
              value={newPnDesc}
              onChange={e => setNewPnDesc(e.target.value)}
            />
            <button type="button" className="pnl-btn-add" onClick={onAdd} {...captureProps(onAdd)}>
              Add
            </button>
          </div>
        )}
      </div>
    </>
  );
}
